































































































































































import { Component, Mixins } from "vue-property-decorator";
import BaseServiceMixin from "../mixins/base.service.mixin";
import WorkerReportModel from "../models/WorkerReportModel";
import DateFilter from "@/components/DateFilter.vue";
import ReportService from "../services/report.service";
import { ApexOptions } from "apexcharts";
import Colours from "../common/Colours";
import DepartmentModel from "../models/DepartmentModel";
import DepartmentService from "../services/department.service";
import eventBus from "../common/bus";
import moment from "moment";

@Component({
  components: {
    DateFilter,
  },
})
export default class WorkerReport extends Mixins(BaseServiceMixin) {
  private reportService!: ReportService;
  private departmentService!: DepartmentService;

  private chartType: "line" | "bar" = "line";

  private companyDepartments: DepartmentModel[] = [];
  private companyDepartment: DepartmentModel | null = null;

  private startDate: Date = new Date();
  private endDate: Date = new Date();

  private reports: WorkerReportModel[] = [];
  private selectedDepartments: Array<{ id: string | number; name: string }> = [];
  private filteredDepartments: Array<{ id: string | number; name: string }> = [];

  private pieChartTitle = "";

  private get colors(): string[] {
    return Colours.getColours(this.reportDepartments.length || 3);
  }

  private get chartOptions(): ApexOptions {
    return {
      chart: {
        id: Math.random()
          .toString(36)
          .substring(7),
        type: this.chartType,
        toolbar: {
          show: true,
          tools: {
            download: false,
            selection: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: false,
            reset: true,
          },
        },
      },
      colors: this.colors,
      xaxis: {
        type: "datetime",
        labels: {
          datetimeFormatter: {
            year: "yyyy",
            month: "MMM 'yy",
            day: "dd MMM",
            hour: "htt",
          },
        },
      },
      noData: {
        text: "No data available for the chosen date range",
        align: "center",
        verticalAlign: "middle",
        offsetX: 0,
        offsetY: 0,
        style: {
          color: "Tomato",
          fontSize: "14px",
          fontFamily: undefined,
        },
      },
      legend: {
        show: true,
      },
    };
  }

  private get pieChartOptions(): ApexOptions {
    return {
      labels: ["Inbound", "Outbound", "Missed"],
      chart: {
        id: Math.random()
          .toString(36)
          .substring(7),
        type: "pie",
      },
      legend: {
        show: true,
      },
    };
  }

  private get pieSeries(): ApexNonAxisChartSeries {
    if (this.reportDepartments.length && this.selectedDepartments.length === 1) {
      const result: WorkerReportModel = this.reportDepartments[0].data.reduce((a, b) => {
        return {
          inboundCalls: a.inboundCalls + b.inboundCalls,
          outboundCalls: a.outboundCalls + b.outboundCalls,
          callsTimedOut: a.callsTimedOut + b.callsTimedOut,
          callsAccepted: a.callsAccepted + b.callsAccepted,
          callsCanceled: a.callsCanceled + b.callsCanceled,
          callsRescinded: a.callsRescinded + b.callsRescinded,
          totalCallDuration: a.totalCallDuration + b.totalCallDuration,
          averageCallDuration: a.averageCallDuration + b.averageCallDuration,
          departmentId: a.departmentId,
          departmentName: a.departmentName,
          workerSid: a.workerSid,
          name: a.name,
          startTime: b.startTime < a.startTime ? b.startTime : a.startTime,
          endTime: b.endTime > a.endTime ? b.endTime : a.endTime,
          lastModified: new Date(),
        };
      });
      this.pieChartTitle = this.reportDepartments[0].name;

      const inboundCalls = result.inboundCalls || 0;
      const outboundCalls = result.outboundCalls || 0;
      const callsTimedOut = result.callsTimedOut || 0;
      if (inboundCalls === 0 && outboundCalls === 0 && callsTimedOut === 0) {
        return [];
      }
      return [inboundCalls, outboundCalls, callsTimedOut];
    }
    return [];
  }

  private get departments(): Array<{ id: string | number; name: string }> {
    const departments: Array<{ id: string | number; name: string }> = [];

    this.reports.forEach((item) => {
      const id: string | number = this.companyDepartment ? item.workerSid : item.departmentId;
      const name = this.companyDepartment ? item.name : item.departmentName;

      if (!departments.map((d) => d.id).includes(id)) {
        departments.push({ id, name });
      }
    });
    return departments;
  }

  private get reportDepartments(): Array<{ id: string | number; name: string; data: WorkerReportModel[] }> {
    if (!this.reports.length) {
      return [];
    }

    let departments = this.departments;

    const selectedDepartmentsIds = this.selectedDepartments.map((d) => d.id);
    if (selectedDepartmentsIds.length) {
      departments = this.departments.filter((department) => selectedDepartmentsIds.includes(department.id));
    }

    this.filteredDepartments = this.departments.filter((department) => !selectedDepartmentsIds.includes(department.id));

    return departments.map((item) => {
      return {
        ...item,
        data: this.reports.filter((internal) => {
          if (this.companyDepartment) {
            return internal.workerSid === item.id;
          }
          return internal.departmentId === item.id;
        }),
      };
    });
  }

  private get outBoundSeries(): ApexAxisChartSeries {
    const outBoundSeries: ApexAxisChartSeries = [];
    this.reportDepartments.forEach((department) => {
      outBoundSeries.push({
        data: department.data.map((d) => {
          return {
            x: moment
              .utc(d.startTime)
              .toDate()
              .getTime(),
            y: d.outboundCalls,
          };
        }),
        name: department.name,
      });
    });
    return outBoundSeries;
  }

  private get inBoundSeries(): ApexAxisChartSeries {
    const inBoundSeries: ApexAxisChartSeries = [];

    this.reportDepartments.forEach((department) => {
      inBoundSeries.push({
        data: department.data.map((d) => {
          return {
            x: moment
              .utc(d.startTime)
              .toDate()
              .getTime(),
            y: d.inboundCalls,
          };
        }),
        name: department.name,
      });
    });
    return inBoundSeries;
  }

  private get missedSeries(): ApexAxisChartSeries {
    const missedSeries: ApexAxisChartSeries = [];

    this.reportDepartments.forEach((department) => {
      missedSeries.push({
        data: department.data.map((d) => {
          return {
            x: moment
              .utc(d.startTime)
              .toDate()
              .getTime(),
            y: d.callsTimedOut,
          };
        }),
        name: department.name,
      });
    });
    return missedSeries;
  }

  private get callDurationSeries(): ApexAxisChartSeries {
    const callDurationSeries: ApexAxisChartSeries = [];

    this.reportDepartments.forEach((department) => {
      callDurationSeries.push({
        data: department.data.map((d) => {
          return {
            x: moment
              .utc(d.startTime)
              .toDate()
              .getTime(),
            y: d.averageCallDuration.toFixed(1),
          };
        }),
        name: department.name,
      });
    });
    return callDurationSeries;
  }

  public created() {
    this.reportService = new ReportService(this.axiosInstance);
    this.departmentService = new DepartmentService(this.axiosInstance);
  }

  private async mounted() {
    await this.getDepartments();
  }

  private getFilteredDepartments(text: string) {
    if (text) {
      this.filteredDepartments = this.departments.filter((option) =>
        option.name.toLowerCase().includes(text.toLowerCase()),
      );
    } else {
      this.filteredDepartments = this.departments;
    }
  }

  private async getDepartments() {
    try {
      this.companyDepartments = await this.departmentService.getDepartments();

      const otherDepartment: DepartmentModel = new DepartmentModel();
      otherDepartment.id = -1;
      otherDepartment.name = "Others";
      this.companyDepartments.push(otherDepartment);
    } catch (error) {
      // Handle error
    }
  }

  private async getReports() {
    try {
      eventBus.$emit("loading", true);
      this.selectedDepartments = [];
      const params: object = {
        startDate: moment(this.startDate).format("YYYY-MM-DD"),
        endDate: moment(this.endDate).format("YYYY-MM-DD"),
      };
      if (this.companyDepartment) {
        Object.assign(params, {
          departmentId: this.companyDepartment.id,
        });
      }
      this.reports = await this.reportService.getWorkerReport(params);
    } catch (error) {
      this.reports = [];
    } finally {
      eventBus.$emit("loading", false);
    }
  }

  private async handleDateSelectionChange(changedDates: Date[]) {
    const start = changedDates[0];
    let end = changedDates[0];
    if (changedDates.length > 1) {
      end = changedDates[1];
    }
    this.startDate = start;
    this.endDate = end;
    await this.getReports();
  }

  private chartTypeChange(isLine: boolean) {
    if (isLine) {
      this.chartType = "line";
    } else {
      this.chartType = "bar";
    }
  }
}
